import React, { useState } from 'react';
import { Card, Button, Table, Alert, Spinner, Modal, Form, Badge } from 'react-bootstrap';
import { useGoogleMeet } from '../auth/GoogleMeetProvider';
import '../styles/brand.css';

const GoogleMeetAttendanceSync = ({
  cohortId,
  onSyncComplete,
  onSync,
  show = null,
  onHide = null,
}) => {
  const {
    isGoogleAuthenticated,
    signInWithGoogle,
    googleMeetService,
    loading: authLoading,
    isConfigured,
  } = useGoogleMeet();

  const [csvRecords, setCsvRecords] = useState([]);
  const [reconciledData, setReconciledData] = useState([]);
  const [manualAttendance, setManualAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [attendanceThreshold, setAttendanceThreshold] = useState(15);
  const [lowAttendanceThreshold, setLowAttendanceThreshold] = useState(70);

  const syncCallback = onSyncComplete || onSync;

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const result = googleMeetService
        ? googleMeetService.parseMeetAttendanceCsv(evt.target.result)
        : { success: false, error: 'Service not available' };
      if (result.success) {
        setCsvRecords(result.data);
        setError(null);
      } else {
        setError(result.error);
        setCsvRecords([]);
      }
    };
    reader.readAsText(file);
  };

  const determineStatus = (record) => {
    if (record.durationMinutes >= attendanceThreshold) return 'present';
    if (record.durationMinutes >= 5) return 'incomplete';
    return 'absent';
  };

  const handleSync = () => {
    if (!csvRecords.length) return;
    const processedData = csvRecords.map((r) => ({
      learnerId: r.email || r.displayName,
      learnerName: r.displayName,
      email: r.email,
      date: new Date().toISOString().split('T')[0],
      status: determineStatus(r),
      durationMinutes: r.durationMinutes,
      source: 'meet-csv',
    }));
    if (syncCallback) syncCallback(processedData);
    alert(`Synced ${processedData.length} attendance records!`);
  };

  const loadManualAttendance = () => {
    setManualAttendance([]);
    alert('Paste or load your LMS attendance list to reconcile.');
  };

  const performReconciliation = () => {
    if (!csvRecords.length || !manualAttendance.length) {
      setError('Both Meet CSV and manual attendance are required for reconciliation.');
      return;
    }
    setLoading(true);
    const result = googleMeetService.reconcileAttendance(
      manualAttendance,
      csvRecords,
      attendanceThreshold
    );
    if (result.success) {
      setReconciledData(result.data);
      setShowReconcileModal(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const saveReconciledData = () => {
    setShowReconcileModal(false);
    setReconciledData([]);
    alert('Reconciled attendance saved!');
  };

  const getStatusBadge = (status, isLowAttendance = false) => {
    const map = {
      present: { variant: isLowAttendance ? 'warning' : 'success', text: isLowAttendance ? 'Present (Low)' : 'Present' },
      absent: { variant: 'danger', text: 'Absent' },
      incomplete: { variant: 'info', text: 'Incomplete' },
      excused: { variant: 'warning', text: 'Excused' },
    };
    const cfg = map[status] || { variant: 'secondary', text: status };
    return <Badge bg={cfg.variant}>{cfg.text}</Badge>;
  };

  if (!isConfigured) {
    return (
      <Card>
        <Card.Body className="text-center">
          <Card.Title>Google Meet Attendance</Card.Title>
          <Alert variant="warning">
            REACT_APP_GOOGLE_CLIENT_ID is not configured. Please contact your administrator.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!isGoogleAuthenticated) {
    return (
      <Card>
        <Card.Body className="text-center">
          <Card.Title>Google Meet Attendance</Card.Title>
          <Card.Text>
            Connect your Google account to create meetings and sync attendance from Google Meet CSV exports.
            Learners present for {attendanceThreshold}+ minutes are automatically marked present.
          </Card.Text>
          <Button
            onClick={() => {
              const result = signInWithGoogle();
              if (result && !result.success) alert(result.error);
            }}
            disabled={authLoading}
            variant="primary"
          >
            {authLoading ? <Spinner size="sm" /> : 'Connect Google Account'}
          </Button>
          <div className="mt-3 text-muted small">
            <strong>How to get a Meet attendance CSV:</strong>
            <ul className="mt-2 text-start">
              <li>After a Google Meet session, open the calendar event</li>
              <li>Click "View attendance report" (available for meeting hosts)</li>
              <li>Download the CSV and upload it here</li>
            </ul>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const content = (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <h5>Google Meet Attendance Sync</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

          <div className="row mb-3">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Presence Threshold (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  value={attendanceThreshold}
                  onChange={(e) => setAttendanceThreshold(parseInt(e.target.value) || 15)}
                  min="5" max="120"
                />
                <Form.Text className="text-muted">
                  Learners present for ≥{attendanceThreshold} min are marked present
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Low Attendance Flag (%)</Form.Label>
                <Form.Control
                  type="number"
                  value={lowAttendanceThreshold}
                  onChange={(e) => setLowAttendanceThreshold(parseInt(e.target.value) || 70)}
                  min="50" max="100"
                />
                <Form.Text className="text-muted">
                  Flag learners below {lowAttendanceThreshold}% session time
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Upload Google Meet Attendance CSV</Form.Label>
            <Form.Control type="file" accept=".csv" onChange={handleCsvUpload} />
            <Form.Text className="text-muted">
              Download the attendance report from your Google Meet calendar event after the session.
            </Form.Text>
          </Form.Group>

          {csvRecords.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6>Meet Attendance ({csvRecords.length} participants)</h6>
                <div>
                  <Button variant="success" size="sm" className="me-2" onClick={handleSync}>
                    Sync to LMS
                  </Button>
                  <Button variant="secondary" size="sm" className="me-2" onClick={loadManualAttendance}>
                    Load Manual Data
                  </Button>
                  <Button variant="primary" size="sm" onClick={performReconciliation} disabled={!manualAttendance.length || loading}>
                    Reconcile
                  </Button>
                </div>
              </div>

              <Table size="sm" striped bordered>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Duration (min)</th>
                    <th>Auto Status</th>
                  </tr>
                </thead>
                <tbody>
                  {csvRecords.map((r, i) => {
                    const status = determineStatus(r);
                    const isLow = r.durationMinutes < (r.sessionDuration * lowAttendanceThreshold / 100);
                    return (
                      <tr key={i} className={
                        status === 'present' ? (isLow ? 'table-warning' : 'table-success') :
                        status === 'incomplete' ? 'table-info' : 'table-light'
                      }>
                        <td>{r.displayName}</td>
                        <td>{r.email}</td>
                        <td>{r.durationMinutes}</td>
                        <td>{getStatusBadge(status, isLow)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <small className="text-muted">
                <strong>Legend: </strong>
                <Badge bg="success" className="me-1">Present (≥{attendanceThreshold}min)</Badge>
                <Badge bg="warning" className="me-1">Present (Low)</Badge>
                <Badge bg="info" className="me-1">Incomplete</Badge>
                <Badge bg="danger">Absent</Badge>
              </small>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showReconcileModal} onHide={() => setShowReconcileModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attendance Reconciliation Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Learner</th>
                <th>Manual Status</th>
                <th>Meet Present</th>
                <th>Duration (min)</th>
                <th>Final Status</th>
                <th>Discrepancy</th>
              </tr>
            </thead>
            <tbody>
              {reconciledData.map((r, i) => (
                <tr key={i} className={r.discrepancy ? 'table-warning' : ''}>
                  <td>{r.learnerName}</td>
                  <td>{getStatusBadge(r.manualStatus)}</td>
                  <td>
                    <Badge bg={r.meetPresent ? 'success' : 'danger'}>
                      {r.meetPresent ? 'Yes' : 'No'}
                    </Badge>
                  </td>
                  <td>{r.meetDurationMinutes}</td>
                  <td>{getStatusBadge(r.finalStatus)}</td>
                  <td>{r.discrepancy && <Badge bg="warning">⚠️ Review</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReconcileModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveReconciledData} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Save Reconciled Data'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  if (show === null) return content;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Google Meet Attendance Sync</Modal.Title>
      </Modal.Header>
      <Modal.Body>{content}</Modal.Body>
    </Modal>
  );
};

export default GoogleMeetAttendanceSync;
